import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PsyGestionLogoFull } from "@/components/PsyGestionLogo";
import { usePageTitle } from "@/hooks/usePageTitle";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", bg: "#F4F8FC", border: "#D6E4F0",
  muted: "#6B85A3", white: "#FFFFFF", danger: "#E63946",
};

export default function Auth() {
  const [searchParams] = useSearchParams();
  // mode=login → connexion, mode=signup → inscription, aucun param → inscription (priorité croissance)
  const [isLogin, setIsLogin] = useState(searchParams.get("mode") === "login");
  usePageTitle(isLogin ? "Connexion" : "Créer un compte gratuit");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    if (isLogin) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
      else navigate("/dashboard");
    } else {
      const appUrl = import.meta.env.VITE_APP_URL || "https://dashboard.freedomarchitect.io";
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${appUrl}/dashboard` },
      });
      if (error) setError(error.message);
      else setMessage("Vérifiez votre email pour confirmer votre inscription.");
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError("");
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${import.meta.env.VITE_APP_URL}/auth/callback`,
    });
    setForgotLoading(false);
    if (error) {
      setForgotError("Email introuvable — vérifiez votre adresse.");
    } else {
      setForgotSent(true);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotEmail("");
    setForgotError("");
    setForgotSent(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: C.bg }}>
      <div className="w-full max-w-md p-8 rounded-2xl shadow-lg border" style={{ backgroundColor: C.white, borderColor: C.border }}>
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <PsyGestionLogoFull height={44} />
          </div>
          <p className="text-sm mt-1" style={{ color: C.muted }}>
            {isLogin ? "Connectez-vous à votre espace" : "Créez votre compte"}
          </p>
          <p className="text-xs mt-2" style={{ color: C.teal, fontWeight: 600 }}>
            Inscription gratuite · Aucun mode de paiement requis
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: C.navy }}>Email</label>
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2"
              style={{ borderColor: C.border }}
              placeholder="votre@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: C.navy }}>Mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 pr-11 rounded-xl border text-sm outline-none focus:ring-2"
                style={{ borderColor: C.border }}
                placeholder="••••••••"
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: C.muted }}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {isLogin && (
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="mt-1.5 text-xs transition-colors"
                style={{ color: C.muted }}
                onMouseEnter={e => (e.currentTarget.style.color = C.teal)}
                onMouseLeave={e => (e.currentTarget.style.color = C.muted)}
              >
                Mot de passe oublié ?
              </button>
            )}
          </div>

          {error && <div className="text-sm p-3 rounded-xl" style={{ backgroundColor: "#FFF0F1", color: C.danger }}>{error}</div>}
          {message && <div className="text-sm p-3 rounded-xl" style={{ backgroundColor: "#E8F5F4", color: C.teal }}>{message}</div>}

          <button
            type="submit" disabled={loading}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: C.teal }}
          >
            {loading ? "Chargement..." : isLogin ? "Se connecter" : "S'inscrire"}
          </button>
        </form>

        <div className="text-center mt-4">
          <button onClick={() => { setIsLogin(!isLogin); setError(""); setMessage(""); }}
            className="text-sm font-medium" style={{ color: C.teal }}>
            {isLogin ? "Pas de compte ? S'inscrire" : "Déjà un compte ? Se connecter"}
          </button>
        </div>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(27, 58, 92, 0.5)" }}
          onClick={closeForgot}
        >
          <div
            className="w-full rounded-2xl shadow-xl p-8 relative"
            style={{ maxWidth: 440, backgroundColor: C.white }}
            onClick={e => e.stopPropagation()}
          >
            {!forgotSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div className="text-center">
                  <div className="text-3xl mb-2">🔐</div>
                  <h2 className="text-lg font-bold" style={{ color: C.navy }}>
                    Réinitialiser votre mot de passe
                  </h2>
                  <p className="text-xs mt-1" style={{ color: C.muted }}>
                    Entrez votre adresse email et nous vous enverrons un lien de réinitialisation.
                  </p>
                </div>
                <div>
                  <label
                    className="block text-[10px] font-bold tracking-wider mb-1.5"
                    style={{ color: C.muted }}
                  >
                    ADRESSE EMAIL
                  </label>
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={e => { setForgotEmail(e.target.value); setForgotError(""); }}
                    className="w-full px-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2"
                    style={{ borderColor: C.border }}
                    placeholder="votre@email.com"
                  />
                  {forgotError && (
                    <p className="text-xs mt-1.5" style={{ color: C.danger }}>{forgotError}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: C.teal }}
                >
                  {forgotLoading ? (
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Envoyer le lien de réinitialisation →"
                  )}
                </button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={closeForgot}
                    className="text-xs transition-colors"
                    style={{ color: C.muted }}
                    onMouseEnter={e => (e.currentTarget.style.color = C.teal)}
                    onMouseLeave={e => (e.currentTarget.style.color = C.muted)}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="text-3xl">✅</div>
                <h2 className="text-lg font-bold" style={{ color: C.navy }}>Email envoyé !</h2>
                <p className="text-sm" style={{ color: C.muted }}>
                  Un lien de réinitialisation a été envoyé à <strong style={{ color: C.navy }}>{forgotEmail}</strong>.
                  Vérifiez votre boîte mail et vos spams.
                </p>
                <button
                  onClick={closeForgot}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white"
                  style={{ backgroundColor: C.teal }}
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
